/**
 * Client-side Logger Utility
 * Provides consistent logging with environment-aware behavior
 */

const isDevelopment = import.meta.env.MODE === 'development'

/**
 * Log levels
 */
const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
}

/**
 * Format log message with timestamp
 */
function formatMessage(level, message) {
  const timestamp = new Date().toISOString()
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`

  return `${prefix} ${message}`
}

function emitLogEvent(level, message, data) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return
  }

  window.dispatchEvent(new CustomEvent('x3algo:log', {
    detail: {
      level,
      message,
      data,
      timestamp: new Date().toISOString()
    }
  }))
}

/**
 * Logger class
 */
class Logger {
  constructor() {
    this.enabled = true
  }

  /**
   * Debug level logging (development only)
   */
  debug(message, data) {
    if (!this.enabled || !isDevelopment) return

    emitLogEvent(LogLevel.DEBUG, formatMessage(LogLevel.DEBUG, message), data)
  }

  /**
   * Info level logging
   */
  info(message, data) {
    if (!this.enabled) return
    
    if (isDevelopment) {
      emitLogEvent(LogLevel.INFO, formatMessage(LogLevel.INFO, message), data)
    } else {
      // In production, send to analytics or monitoring service
      this._sendToMonitoring(LogLevel.INFO, message, data)
    }
  }

  /**
   * Warning level logging
   */
  warn(message, data) {
    if (!this.enabled) return
    
    if (isDevelopment) {
      emitLogEvent(LogLevel.WARN, formatMessage(LogLevel.WARN, message), data)
    } else {
      // In production, send to monitoring service
      this._sendToMonitoring(LogLevel.WARN, message, data)
    }
  }

  /**
   * Error level logging
   */
  error(message, error, data) {
    if (!this.enabled) return
    
    const errorData = {
      message: error?.message,
      stack: error?.stack,
      ...data
    }
    
    if (isDevelopment) {
      emitLogEvent(LogLevel.ERROR, formatMessage(LogLevel.ERROR, message), errorData)
    } else {
      // In production, send to error tracking service
      this._sendToMonitoring(LogLevel.ERROR, message, errorData)
    }
  }

  /**
   * Log API request
   */
  apiRequest(method, url) {
    if (!isDevelopment) return
    this.debug(`🔄 API Request: ${method.toUpperCase()} ${url}`)
  }

  /**
   * Log API response
   */
  apiResponse(status, url) {
    if (!isDevelopment) return
    this.debug(`✅ API Response: ${status} ${url}`)
  }

  /**
   * Log API error
   */
  apiError(method, url, error) {
    this.error(`API Error: ${method.toUpperCase()} ${url}`, error)
  }

  /**
   * Log performance metric
   */
  performance(metric) {
    if (!isDevelopment) return
    this.debug('Performance Metric:', metric)
  }

  /**
   * Log user action
   */
  userAction(action, details) {
    this.info(`User Action: ${action}`, details)
  }

  /**
   * Send logs to monitoring service in production
   * This is a placeholder - integrate with your monitoring service
   */
  _sendToMonitoring(_level, _message, _data) {
    // In production, you would send logs to a service like:
    // - Sentry
    // - LogRocket
    // - Datadog
    // - Custom analytics endpoint
    
    // For now, we'll just suppress console output in production
    // to keep the console clean for end users
    
    // Example integration:
    // if (window.Sentry) {
    //   window.Sentry.captureMessage(message, {
    //     level,
    //     extra: data
    //   })
    // }
  }

  /**
   * Disable logging (useful for tests)
   */
  disable() {
    this.enabled = false
  }

  /**
   * Enable logging
   */
  enable() {
    this.enabled = true
  }
}

// Create singleton instance
const logger = new Logger()

// Export logger instance and utilities
export default logger
export { LogLevel }
